import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { t } from '@ctcells/i18n'

import { Button } from '../atoms/Button.tsx'
import type { DisabledState } from '../atoms/disabled.tsx'
import { useModalFrame } from '../atoms/modalFrame.ts'
import { Card } from './resourceParts.tsx'

import './screens.css'
import './resource.css'
import './addForm.css'

/**
 * **번호 붙은 절** — 만들기 화면의 뼈대.
 *
 * 🔴 Opentrons **Labware Creator** 의 절 구성을 옮긴 것이다
 *    (`labware-library/src/labware-creator/components/sections/SectionBody.tsx:12-18` ·
 *    `sections/Grid.tsx:19-40`). 저쪽 한 절은 ⓐ 제목 ⓑ 설명 문단 ⓒ 도해 ⓓ 입력 ⓔ 그 절의
 *    검증 알림 다섯으로 되어 있고, **한 장짜리 스크롤 폼**이지 단계별 마법사가 아니다.
 *    염색기가 Opentrons 계열 장비라 그 장비를 쓰는 사람이 이 흐름에 익숙하다
 *    (근거 전문은 `docs/opentrons-alignment.md` §3-3).
 *
 * 🔴 **마법사(다음/이전)로 만들지 않는다.** Opentrons 도 한 장이다 — 절을 오가며 고치는
 *    일이 잦은 폼이라 단계로 가두면 되레 느리다. 번호는 «순서 안내»이지 잠금이 아니다.
 * 🔴 **도해 칸은 «있을 때만» 둔다.** Opentrons 는 절마다 그림이 있지만 우리에겐 격자 도해
 *    하나뿐이다 — 빈 도해 자리를 깔지 않는다.
 *
 * ⚠ 제목은 `h3` 다. 상세 화면의 `Card` 제목(`div`)과 달리 **문서 구조로 읽혀야** 한다 —
 *   절이 여럿인 한 장짜리 폼이라 스크린리더가 목차로 훑을 수 있어야 한다.
 */
export interface FormSectionProps {
  /**
   * 1부터. 화면에 보이는 번호이자 순서 안내다 — **페이지에서만** 보인다. 창(`AddModal`) 안에서는 장식을 걷고
   * 절이 놓인 차례가 말한다. 번호가 없는 절(레시피 · 창 전용 폼)은 넘기지 않는다
   */
  step?: number | undefined
  /** 절 제목. 첫 절(이름 한 칸 등)은 제목 없이 설 수 있다 */
  title?: ReactNode
  /** 한 줄 안내. Opentrons 의 `instructions_column` 자리다 */
  help?: string | undefined
  /** 🔴 그 절의 문제만 여기 적는다. 화면 맨 위로 모으지 않는다(어느 칸인지 사라진다) */
  issue?: string | undefined
  /** 오른쪽 도해. 없으면 칸을 만들지 않는다 */
  figure?: ReactNode
  /**
   * 칸 폭 상한(`--fsec-fields-w`)을 푼다 — 표 · 편집기처럼 여러 열이 한 줄에 서는 절. 상한은 «라벨 ↔ 값» 한 줄을 위한
   * 것이라 그 안에 가두면 열이 눌린다. 창 안은 원래 상한이 없다(창 폭이 곧 칸 폭)
   */
  wide?: boolean | undefined
  /**
   * 제목 줄 오른끝 단추(작업 추가 창 시료 구역의 「기존 시료에서」 · 「새 시료 추가」 · 2026-10-21). 🔴 제목(`h3`) «밖»에 선다 —
   * 제목의 읽는 이름에 단추 글자가 섞이지 않게. 안 넘기면 DOM 이 종전 그대로다
   */
  actions?: ReactNode
  children: ReactNode
}

/**
 * 🔴 **「추가」 창 일곱의 구획은 이것 하나다**(eric 2026-09-30 「창마다 포맷이 달라.. 다 통일해줘」). 종전에는 세 벌
 *    (`.sect` 구역 · `.fsec` 절 · `.card` 카드)이 섞여 있었다. 창 안에서는 번호 · 테두리 · 카드 없이 굵은 작은 제목 +
 *    여백으로 선다(`addForm.css`).
 */
export function FormSection({
  step,
  title,
  help,
  issue,
  figure,
  wide,
  actions,
  children,
}: FormSectionProps) {
  const heading =
    title === undefined ? null : (
      <h3 className="fsec__title">
        {step === undefined ? null : (
          <span aria-hidden="true" className="fsec__num">
            {String(step)}
          </span>
        )}
        {title}
      </h3>
    )
  return (
    <section className={wide === true ? 'fsec fsec--wide' : 'fsec'}>
      {actions === undefined ? (
        heading
      ) : (
        <div className="fsec__head">
          {heading}
          <div className="fsec__actions">{actions}</div>
        </div>
      )}
      {help === undefined ? null : <p className="fsec__help">{help}</p>}
      {issue === undefined ? null : (
        <p className="vissue" role="alert">
          {issue}
        </p>
      )}
      <div className={figure === undefined ? 'fsec__body' : 'fsec__body fsec__body--fig'}>
        <div className="fsec__fields">{children}</div>
        {figure === undefined ? null : <div className="fsec__fig">{figure}</div>}
      </div>
    </section>
  )
}

/**
 * 짧은 칸 둘 · 셋을 한 줄에(실시 시각/유효기한 · 계정 ID/이름 · 표시 이름/정의 ID).
 * 🔴 **짧은 칸 쌍에만 쓴다** — 폼의 기본은 한 열이다. 열은 같은 폭이고, 칸은 그 폭을 다 쓴다.
 */
export function FormRow({ cols = 2, children }: { cols?: 2 | 3 | 4; children: ReactNode }) {
  return <div className={`frow frow--${String(cols)}`}>{children}</div>
}

/**
 * 짝이 없는 짧은 칸 하나(격자 · 용량 · 개수 · 기준점 ID 같은 몇 글자짜리) — **창 안에서만** 한 줄의 1/3 폭으로 선다.
 * 🔴 창을 넓힌 뒤(eric 2026-09-30 「너무 좁아」) 「3×3」 칸이 창 끝까지 늘어져 휑했다.
 *    폭은 `FormRow cols={3}` 한 열과 같아 같은 창의 3칸 줄과 줄이 맞는다(`addForm.css` `.fshort`).
 * ⚠ 페이지(장비 관점 콘솔)에서는 아무 일도 안 하는 감싸개다 — 거기는 절의 칸 폭 상한이 이미 있다.
 * 긴 글 칸(이름 · 메모 · 목록)에는 쓰지 않는다.
 */
export function FormShort({ children }: { children: ReactNode }) {
  return <div className="fshort">{children}</div>
}

/**
 * 칸 · 절 아래 한 줄 옅은 도움말 — 「비우면 자동」 · 「확인 중」 같은 «지금 상태» 한 줄. 종전의 `.muted` 문단 ·
 * 세로줄 `.snnote` 둘을 이것 하나로 모았다. `role` 은 부르는 쪽이 정한다(바뀌는 상태면 `status`).
 */
export function FormHint({
  children,
  id,
  role,
}: {
  children: ReactNode
  id?: string | undefined
  role?: 'status' | 'note' | undefined
}) {
  return (
    <p className="fhint" id={id} role={role}>
      {children}
    </p>
  )
}

/**
 * 서버가 거부한 까닭 한 줄 — **페이지에서만** 그 자리에 선다. 창 안에서는 `AddActions` 의 `error` 가 바닥 줄 왼쪽에
 * 세운다(본문을 어디까지 내렸든 「추가」 옆에서 보인다 · 규격 「에러는 한 자리」). 둘 다 같은 글자를 받는다.
 */
export function FormError({ message }: { message?: ReactNode }) {
  const frame = useModalFrame()
  if (frame !== null || message === undefined || message === null) return null
  return (
    <p className="vissue" role="alert">
      {message}
    </p>
  )
}

/**
 * 보조 정보 한 덩이(미리보기 · 만들어질 것 · 고칠 것 · 작업 배정) — 페이지에서는 **오른쪽 요약 칸의 카드**, 창 안에서는
 * **흐름 맨 아래의 구획**(`FormSection`)이다(추가 창 규격 6 · eric 2026-09-30). 두 벌을 화면마다 가르지 않게 여기서 가른다.
 */
export function FormAside({ title, children }: { title: string; children: ReactNode }) {
  const frame = useModalFrame()
  if (frame !== null) return <FormSection title={title}>{children}</FormSection>
  return <Card title={title}>{children}</Card>
}

export interface AddActionsProps {
  onCancel: () => void
  /** 보내는 중 — 🔴 취소도 막는다(창이 닫히면 결과를 못 본다). 창의 × · Esc · 바깥 누름은 `useModalBusy` 가 막는다 */
  busy: boolean
  /** 주 버튼의 선행조건(`gate` 결과). 비활성이면 사유가 같이 나간다 */
  submitGate: DisabledState
  onSubmit: () => void
  /** 🔴 **창 밖(페이지)** 의 주 버튼 글자 — 장비 관점 콘솔의 페이지는 종전 글자 그대로다 */
  pageLabel: string
  /**
   * 창 안 주 버튼 글자 — 안 주면 「추가」(추가 창 공통). 🔴 시료 추가 창만 「확인」이다(eric 2026-10-13 「맨 밑에 최종 확인에서..
   * 취소 그리고 확인 버튼으로」 — 그 창은 왼쪽 「+ 추가」가 목록에 넣고, 바닥 버튼은 목록 전체를 등록하는 «최종 확인»이라서)
   */
  modalLabel?: string | undefined
  /** 주 버튼 옆 보조 글자(보낼 건수 등). 창 안에서만 선다 — 페이지는 `pageLabel` 이 이미 말한다 */
  note?: string | undefined
  /** 창 안인가. 기본은 창 문맥(`useModalFrame`)으로 잰다. 창 틀을 prop 으로 받는 화면(새 작업)이 넘긴다 */
  framed?: boolean | undefined
  /**
   * 서버가 거부한 까닭(422 본문 · 409 등). 🔴 **창 안에서만** 바닥 줄 왼쪽에 선다 — 「추가」 옆이라 본문 스크롤과
   * 무관하게 보인다. 페이지는 화면이 `FormError` 로 제 자리에 세운다
   */
  error?: ReactNode
  /**
   * 바닥 줄에 «글자로는» 세우지 않을 비활성 사유(사유 문자열 그대로). 버튼의 툴팁 · 보조기술 사유는 그대로다 — 눈에 늘 보이는 글자만 뺀다.
   * 작업 추가 창이 「레시피와 시료가 있어야 합니다」를 넘긴다(eric 2026-10-21 「이거 빼줘 텍스트」 — 칸표 · 잠금 사유가 이미 화면에 말한다).
   * 다른 사유(제어락 · 권한 · 보내는 중 · 칸 넘침)는 계속 선다
   */
  quietReasons?: readonly string[] | undefined
}

/**
 * 「추가」 창의 바닥 줄 — **「취소」(고스트) · 「추가」(주 버튼)** 둘(eric 2026-09-30 「포맷을 모두 동일하게」).
 * 추가 창 일곱이 이것을 쓴다. 종전에는 「작업 생성」 · 「N건 추가」 · 「만들기」 · 「저장」 · 「계정 만들기」 · 「보충 기록」이었다.
 *
 * 🔴 **창 안에서만 「추가」다.** 같은 폼을 페이지로 쓰는 곳(장비 관점 콘솔)은 `pageLabel` 그대로 선다 —
 *    그 앱은 이번 범위 밖이다. 건수는 창 안에서 버튼 옆 보조 글자(`note`)로 간다.
 * 자리(창 바닥 줄로 옮기기)는 부르는 쪽이 정한다 — `DetailPage actions` 는 저절로, 그 밖은 `ModalFooter`.
 */
export function AddActions({
  onCancel,
  busy,
  submitGate,
  onSubmit,
  pageLabel,
  modalLabel,
  note,
  framed,
  error,
  quietReasons,
}: AddActionsProps) {
  const frame = useModalFrame()
  const inModal = framed ?? frame !== null
  /*
   * 주 버튼이 꺼진 까닭을 버튼 왼쪽에 글자로 — 툴팁(마우스를 올려야 보임)만으로는 안 보였다(eric 2026-10-21 「다 선택을 했는데... 왜.. 추가 버튼이
   * 활성화 안 돼?」 — 제어락이 없었다). 글자는 `submitGate` 의 사유 그대로다(새 문구를 짓지 않는다). 보조 글자(`note` · 건수)와 같은 자리라
   * 사유가 있으면 사유가 선다 — 꺼진 버튼 옆 건수보다 «왜 못 누르나»가 먼저다. 보조기술은 버튼이 이미 사유를 읽는다(여기는 눈에만)
   */
  const blocked =
    submitGate.disabled && !(quietReasons ?? []).includes(submitGate.disabledReason)
      ? submitGate.disabledReason
      : undefined
  return (
    <>
      {inModal && error !== undefined && error !== null ? (
        <p className="vissue addfoot__error" role="alert">
          {error}
        </p>
      ) : null}
      {inModal && blocked !== undefined ? (
        <span aria-hidden="true" className="addfoot__reason">
          {blocked}
        </span>
      ) : inModal && note !== undefined ? (
        <span className="addfoot__note">{note}</span>
      ) : null}
      <Button
        onClick={onCancel}
        size="pill"
        {...(busy
          ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
          : {})}
      >
        {t('action.cancel')}
      </Button>
      <Button onClick={onSubmit} size="pill" variant="primary" {...submitGate}>
        {inModal ? (modalLabel ?? t('action.add')) : pageLabel}
      </Button>
    </>
  )
}

/**
 * 창 바닥 줄로 옮긴다. 창 밖이거나(시험 · 페이지) 바닥 줄 자리가 아직 없으면 **그 자리에 그대로** 선다 —
 * 버튼이 사라지지 않는다. `DetailPage` 를 안 쓰는 폼(새 작업)이 쓴다.
 */
export function ModalFooter({ children }: { children: ReactNode }) {
  const frame = useModalFrame()
  if (frame?.footer === null || frame?.footer === undefined) {
    return <div className="btnrow btnrow--end addfoot--inline">{children}</div>
  }
  return createPortal(children, frame.footer)
}
